import { Apollo, gql } from "apollo-angular";
import { Injectable, inject } from "@angular/core";
import { ActivatedRouteSnapshot } from "@angular/router";

import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Query } from "../../../../codegen/out/types";
import { ExtendedGenericSystem } from "src/typings/extended-generic-system";

const systemQuery = gql`
  query System($id: ID!) {
    system(id: $id) {
      __typename
      id
      modelName
      verboseNames
      comment
      customerVisible
      manufacturers {
        name
        logo {
          schematic {
            url
          }
        }
      }
      thumbnail {
        schematic {
          url
        }
        realLife {
          url
        }
      }
      interfaceConfigurations {
        comment
        interface {
          id
          name
          type
          compatibility
          priority
          thermInterfaceTypes {
            id
            name
            configurationMenuId
            heatingCircuitConfigurationRequired
            hotWater
          }
        }
        enabled
        disabledReason
        connector {
          name
          configuration {
            field
            value
          }
        }
        wiring {
          devices
          comment
          terminals {
            device
            system
            optional
          }
          jumperPosition
          bridgePosition
          externalPowerSupplyNeeded
        }
        interfaceActivation
        applicableForTbmFlow
        phyCompatibleDevices
      }
      yellowFlags {
        type
        title
        explanation
        devices {
          shortName
        }
        enabled
      }
      attachments(type: ATTACHMENT) {
        name
        url
        contentType
      }
      installationInstructionsAttachments: attachments(
        type: INSTALLATION_INSTRUCTIONS
      ) {
        name
        url
        contentType
      }
      ... on EffectiveBoilerControl {
        heatingType
        heatPumpType
        powerSupply
      }
      ... on BoilerControl {
        heatingType
        heatPumpType
        powerSupply
        fitInAvailability
        combinedSystems {
          id
          modelName
          manufacturers {
            name
          }
          thumbnail {
            schematic {
              url
            }
          }
          interfaceConfigurations {
            interface {
              name
              compatibility
            }
            enabled
          }
        }
      }
      ... on BoilerControlWithFitsIn {
        heatingType
        heatPumpType
        powerSupply
        boilerControl {
          id
          modelName
          thumbnail {
            schematic {
              url
            }
            realLife {
              url
            }
          }
        }
        fitIn {
          id
          modelName
          thumbnail {
            schematic {
              url
            }
            realLife {
              url
            }
          }
        }
      }
      ... on Boiler {
        powerSupply
        furnace {
          id
          modelName
          thumbnail {
            schematic {
              url
            }
            realLife {
              url
            }
          }
        }
        controlUnit {
          id
          modelName
          thumbnail {
            schematic {
              url
            }
            realLife {
              url
            }
          }
        }
      }
      ... on Furnace {
        powerSupply
      }
      ... on ControlBoard {
        powerSupply
      }
      ... on RoomThermostat {
        powerSupply
      }
      ... on RfRoomThermostat {
        powerSupply
      }
      ... on Remote {
        powerSupply
      }
      ... on RfRemote {
        powerSupply
      }
      ... on RoomThermostatReceiver {
        powerSupply
      }
      ... on AuxiliaryControl {
        powerSupply
      }
      ... on TranslationModule {
        powerSupply
      }
    }
  }
`;

@Injectable()
export class SystemResolver {
  private apollo = inject(Apollo);

  resolve(route: ActivatedRouteSnapshot): Observable<ExtendedGenericSystem> {
    const systemId = route.paramMap.get("systemId");

    return this.apollo
      .query<Query>({
        query: systemQuery,
        variables: {
          id: systemId,
        },
      })
      .pipe(
        map((res) => {
          if (res.errors != null) {
            throw Error("Query failed");
          }

          return res.data.system as ExtendedGenericSystem;
        })
      );
  }
}
